import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
// next
import { useRouter } from 'next/router';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { alpha, styled } from '@mui/material/styles';
import {
  Card,
  Box,
  Typography,
  Stack,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  TextField,
  AccordionDetails,
  Skeleton,
  Divider,
  Radio,
  RadioGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive'
import useIsMountedRef from '../../hooks/useIsMountedRef';
// components
import DIOText from '../../components/DIOText';
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import { FormProvider, RHFTextField, RHFFileOrURL, RHFCheckbox } from '../../components/hook-form';
// utils
import axios from '../../utils/axios';
import {default as axiosNormal} from 'axios';
import { fKrDateYYYYMMKST } from '../../utils/formatTime';
import * as Sentry from '@sentry/nextjs';
// dialogs
import {
  AddCareerDialog,
  ModifyCareerDialog,
  HideCareerDialog,
} from '../profile';
import OfferPassDialog from './OfferPassDialog';
import WorkingTermsDialog from './WorkingTermsDialog';
import EasySalaryDialog from './EasySalaryDialog';
import FeeTaxDialog from './FeeTaxDialog';
import CannotApplyAverageSalaryDialog from './CannotApplyAverageSalaryDialog';
import Check3WayAvailableTime from './Check3WayAvailableTime';
// charts
import AverageSalaryChart from './AverageSalaryChart';
import CircleNumber from '../../components/CircleNumber';
// routes
import { PATH_OFFER } from '../../routes/paths';


// ----------------------------------------------------------------------

const BackGroundStyle = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2.5),
  borderRadius: Number(theme.shape.borderRadius) * 2,
  backgroundColor: theme.palette.grey[100],
}));

const BGAccordion = styled(Accordion)(({ theme }) => ({
  "&:hover": {
    border: '1px solid black',
  },
  padding: theme.spacing(1.5, 0),
  border: `1px solid ${theme.palette.grey[500_32]}`,
  borderRadius: Number(theme.shape.borderRadius),
}));

const NoBGAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  "&.Mui-focusVisible": {
    backgroundColor: "inherit",
  },
  height: 32,
  minHeight: '100% !important',
  padding: theme.spacing(0, 0, 0, 2),
}));


const BGAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 2),
}));

const InfoText = styled('span')(({ theme }) => ({
  color: theme.palette.info.main,
  fontWeight: 'bold',
}));


// ----------------------------------------------------------------------

export default function OfferResponseForm({
  availableHours, matchingHours, dioComment, questions, application, reloadOffer, check3Way, goCheck3Way, backCheck3Way,
  isModify=false, modifyItems, modifyMesage,
  answer_salary, answer_question, answer_suggestion, answer_working_hours, previousTimes=null,
  coffeeChat=null,
  meetingCount=0,
  needPortfolio,
  isOpenJobPost=false,
  openJobPostUUID=null,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { oid } = router.query;
  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');
  const { user } = useAuth();
  const isMountedRef = useIsMountedRef();

  const [isLoading, setIsLoading] = useState(false);
  const [amount, setAmount] = useState({});
  const [detailIndex, setDetailIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 4주 인건비
  const [salary, setSalary] = useState('');
  const [averageApplied, setAverageApplied] = useState(false);
  const [expand, setExpand] = useState(true);

  // 사전질문 YES, NO 답변
  const [answersyn, setAnswersyn] = useState(new Array(questions?.length || 0).fill(null));

  // dialogs
  const [passDialog, setPassDialog] = useState(false);
  const [isOpenWorkingTermsDialog, setIsOpenWorkingTermsDialog] = useState(false);
  const [isOpenFeeTaxDialog, setIsOpenFeeTaxDialog] = useState(false);

  // career dialog
  const [isOpenAddCareerDialog, setIsOpenAddCareerDialog] = useState(false);
  const [isOpenModifyCareerDialog, setIsOpenModifyCareerDialog] = useState(false);
  const [isOpenHideCareerDialog, setIsOpenHideCareerDialog] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState({});
  const [selectedCareerIndex, setSelectedCareerIndex] = useState(null);
  const [hasWorkingCareer, setHasWorkingCareer] = useState(false);

  const [isOpenEasySalaryDialog, setIsOpenEasySalaryDialog] = useState(false);
  const [isOpenCannotApplyAverageSalaryDialog, setIsOpenCannotApplyAverageSalaryDialog] = useState(false);


  const newPositions = ["finance", "operator", "engineer"];

  // 사전 질문
  const hasQuestion = questions?.length > 0;
  const [answersLength, setAnswersLength] = useState(new Array(questions?.length || 0).fill(0));

  // 4주 인건비 필드 control
  const toggleAccordion = () => {
    setExpand((prev) => !prev);
  };

  const isPortfolioRequiredPosition = needPortfolio;
  const portfolioURL = application?.urls?.find(f => f.name === '포트폴리오 링크');
  const portfolioFile = application?.files?.find(f => f.file_type === 'portfolio');
  const portfolioType = portfolioURL ? 'URL' : '파일';

  const openFile = async (item) => {
    const response = await axios.post(`/api/crews/profile/file/${item.id}/url/`);
    window.open(response.data.url);
  };

  const openPassDialog = async () => {
    setPassDialog(true);
  }

  const closePassDialog = async () => {
    setPassDialog(false);
  }

  const OfferResponseSchema = Yup.object().shape({
    careers: Yup.array().required('경력을 입력해주세요').min(1, '경력을 입력해주세요'),
    comment: Yup.string().required('본인 소개를 입력해주세요').min(20, '20자 이상 입력해주세요'),
    workingHoursPerWeek: Yup.number().typeError('근무시간을 숫자로 입력해주세요').positive('양수를 입력해주세요').required('주당 업무 가능한 시간을 입력해주세요').max(availableHours, '현재 업무 가능 시간 이하로 입력해주세요'),
    salaryForCheck: Yup.number().typeError('인건비를 숫자로 입력해주세요'),
    FOUportfolioType: Yup.string(),
    FOUportfolioURL: Yup.string(),
    FOUportfolioFile: Yup.mixed(),
    checkPortfolio: Yup.bool(),
  });

  const defaultValues = {
    careers: user?.crew?.careers || [],
    comment: dioComment || '',
    salaryForCheck: 0,
    workingHoursPerWeek: 0,
    suggestion: '',
    FOUportfolioType: portfolioURL ? 'FOUportfolioURL' : 'FOUportfolioFile',
    FOUportfolioURL: portfolioURL?.url || '',
    FOUportfolioFile: {id: portfolioFile?.id || null, name: portfolioFile?.file_name || '', file: portfolioFile?.file || '', file_type: 'portfolio'},
    checkPortfolio: false,
  };

  const methods = useForm({
    resolver: yupResolver(OfferResponseSchema),
    defaultValues,
  });

  const {
    setValue,
    getValues,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = methods;

  const watchValues = async (value, name, _type) => {
    if (name === 'careers') {
      setHasWorkingCareer(value.careers.some(career => career.end_date === null));
    }
    if (["answers.0", "answers.1", "answers.2"].includes(name)) {
      const lengths = value.answers.map((ans, _) => ans?.trim().length || 0);
      setAnswersLength(lengths);
    }
    
    // 질문 답변 검증 로직 개선
    let hasIncompleteAnswers = false;
    if (hasQuestion && questions?.length > 0) {
      const answers = value.answers || [];
      hasIncompleteAnswers = questions.some((question, idx) => {
        // yes-no 질문의 경우 form value에서 직접 체크
        if (question.question_type === "yes-no") {
          const answerynValue = value[`answeryn-${idx}`];
          if (!answerynValue) {
            return true;
          }
        }
        // 텍스트 답변이 없거나 50자 미만인 경우
        const answer = answers[idx];
        return !answer || ((answer?.trim().length || 0) < 50);
      });
    }

    if (name === 'workingHoursPerWeek') {
      trigger('workingHoursPerWeek');
      setAverageApplied(false);
    };
    if (name === 'comment') {
      trigger('comment');
    };
  };

  useEffect(() => {
    if (answer_salary) {
      setSalary(answer_salary);
      setValue('salaryForCheck', answer_salary);
    }
  }, [answer_salary]);

  useEffect(() => {
    if (answer_working_hours) {
      setValue('workingHoursPerWeek', answer_working_hours);
    }
  }, [answer_working_hours]);

  useEffect(() => {
    if (answer_suggestion) {
      setValue('suggestion', answer_suggestion);
    }
  }, [answer_suggestion]);

  const handleAnswersyn = (value, index) => {
    const updatedAnswersyn = [...answersyn];
    updatedAnswersyn[index] = value;
    setAnswersyn(updatedAnswersyn);
    // form에도 값을 설정
    setValue(`answeryn-${index}`, value);
  };

  const updatePreviousQuestionAnswers = async () => {
    const answersLength = new Array(questions?.length || 0).fill(0);
    answer_question.answers.forEach((ans, i) => {
      const questionIndex = questions?.findIndex(q => q?.topic === ans?.question);
      if (questionIndex >= 0) {
        answersLength[questionIndex] = ans?.answer?.trim().length || 0;
        setValue(`answers.${questionIndex}`, ans?.answer);
        // set answersyn
        if (questions[questionIndex].question_type === "yes-no") {
          setAnswersyn((prev) => {
            const updatedAnswersyn = [...prev];
            updatedAnswersyn[questionIndex] = ans?.yn_answer;
            return updatedAnswersyn;
          });
          // form에도 값을 설정
          setValue(`answeryn-${questionIndex}`, ans?.yn_answer);
        }
      }
    });
    setAnswersLength(answersLength);
  }

  useEffect(() => {
    if (questions?.length > 0 && answer_question?.answers?.length > 0) {
      updatePreviousQuestionAnswers();
    };
  }, [answer_question]);

  useEffect(() => {
    if (dioComment) {
      setValue('comment', dioComment);
    }
  }, [dioComment]);

  useEffect(() => {
    if (isModify) {
      setSalary(answer_salary);
      setValue('salaryForCheck', answer_salary);
      setValue('workingHoursPerWeek', answer_working_hours);
      setValue('suggestion', answer_suggestion);
      if (questions?.length > 0 && answer_question?.answers?.length > 0) {
        updatePreviousQuestionAnswers();
      };
    }
  }, [isModify]);

  useEffect(() => {
    const initialize = async () => {
      try {
        setIsLoading(true);
        if (newPositions.includes(user?.crew?.position)) {
          setIsLoading(false);
        } else {
          const appResponse = await axios.get('api/crews/average-amount/');
          setAmount(appResponse.data);
          appResponse.data.details.forEach((detail, i) => {
            if (detail.max_years >= application?.years_of_experience && detail.min_years <= application?.years_of_experience) {
              setDetailIndex(i);
            }
          })
          setIsLoading(false);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
        console.error(err);
      }
    };

    const dnd = window.localStorage.getItem('dioCrewEasySalaryDialog');
    setIsOpenEasySalaryDialog(!(dnd === 'true') && !(dnd === true));
    initialize();
    trigger(['careers', 'comment']);

    const subscription = watch((value, { name, type }) => watchValues(value, name, type ));
    return () => subscription.unsubscribe();
  }, [watch]);

  const fileUploadBeforeSubmit = async (file) => {
    if (file.id && file.name) {
      return {file_id: file.id, file_name: file.name, file_type: 'portfolio'};
    } else if (file.name === '' || file.name === null) {
      return {}
    } else {
      const fileURLResponse = await axios.post(
        `/api/crews/file-url/`,
        {filename: file.name}
      );
      const {filename, url} = fileURLResponse.data;

      await axiosNormal.put(
        url,
        file,
        {
          headers: {
            'Content-Type': file.type
          }
        }
      );
      return {file_id: 0, file_name: filename, file_type: 'portfolio'};
    }
  };

  const getPortfolioData = async (formData) => {
    if (formData.FOUportfolioType === "FOUportfolioURL") {
      return {
        matching_uuid_to_share: oid || openJobPostUUID,
        file_id: 0,
        file_name: '',
        file_type: '',
        url_name: '포트폴리오 링크',
        url: formData.FOUportfolioURL,
      }
    } else {
      const fileUploadedData = await fileUploadBeforeSubmit(formData.FOUportfolioFile);
      return {
        matching_uuid_to_share: oid || openJobPostUUID,
        url_name: '',
        url: '',
        ...fileUploadedData,
      }
    }
  };

  const getAcceptPostURL = () => {
    if (openJobPostUUID && isOpenJobPost) {
      return `/api/crews/open-job-posts/${openJobPostUUID}/`;
    } else {
      return `/api/crews/offers/${oid}/accept/`;
    }
  }

  const sendAccept = async (times) => {
    try {
      setIsSubmitting(true);
      const data = getValues();
      const postURL = getAcceptPostURL();
      
      // form에서 yes-no 답변들 수집
      const formAnswersyn = questions?.map((_, idx) => data[`answeryn-${idx}`]) || [];
      
      const response = await axios.post(postURL, {
        careers: data.careers,
        comment: data.comment,
        salary_for_4weeks: salary,
        working_hours_per_week: data.workingHoursPerWeek,
        modify_task_proposal: data.suggestion,
        crew_3way_time_range: times,
        questions: questions,
        question_answers: data.answers,
        question_answersyn: formAnswersyn,
      });
      if (response?.data?.dialog === "full") {
        enqueueSnackbar(response?.data?.message, { variant: 'warning' });
        setIsSubmitting(false);
        backCheck3Way();
        reloadOffer();
        return ;
      }
      if (isPortfolioRequiredPosition) {
        const portfolioData = await getPortfolioData(data);
        await axios.patch(
          '/api/crews/career/portfolio/',
          portfolioData,
        )
      };
      if (isOpenJobPost) {
        enqueueSnackbar('공고에 지원하셨습니다.', { variant: 'success' });
        router.push({pathname: PATH_OFFER.root, query: {oid: openJobPostUUID}});
      } else {
        enqueueSnackbar('제안을 수락하셨습니다.', { variant: 'success' });
        setIsSubmitting(false);
        backCheck3Way();
        reloadOffer();
      }
    } catch (error) {
      setIsSubmitting(false);
      Sentry.captureException(error);
      if (error.response && error.response.status === 400 && error.response.data) {
        enqueueSnackbar(error.response.data.message, { variant: 'warning' });
        backCheck3Way();
        reloadOffer();
      } else {
        enqueueSnackbar('저장에 실패하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
      }
      console.error(error);
    }
  };

  const openWorkingTermsDialog = async () => {
    setIsOpenWorkingTermsDialog(true);
  };

  const openFeeTaxDialog = async () => {
    setIsOpenFeeTaxDialog(true);
  };

  const handleSalary = async (e) => {
    setSalary(e.target.value); // 화면에 입력받고 표시하기 위한 값
    setValue('salaryForCheck', e.target.value);  // 유효성 검사를 위한 값
    setAverageApplied(false);
  };

  const checkAverageSalary = async () => {
    if (!averageApplied) {
      const hours = getValues("workingHoursPerWeek");
      if (!hours) {
        enqueueSnackbar("업무 가능한 시간을 입력해주세요", { variant: "warning" });
      }
      else if (hours < 10 || hours > 20) {
        setIsOpenCannotApplyAverageSalaryDialog(true);
      } else {
        const detail = amount?.details?.[detailIndex];
        if (hours < 16) {
          setSalary(detail?.amount_10_15);
          setValue('salaryForCheck', detail?.amount_10_15);
        } else {
          setSalary(detail?.amount_16_20);
          setValue('salaryForCheck', detail?.amount_16_20);
        }
        enqueueSnackbar("평균 인건비가 적용되었습니다");
        setAverageApplied(true);
      }
    } else {
      setAverageApplied(false);
    }
  };

  const openCareerDialog = async () => {
    setIsOpenAddCareerDialog(true);
  };

  const closeAddCareerDialog = async () => {
    setIsOpenAddCareerDialog(false);
  };

  const openModifyCareerDialog = async (selectedCareer, selectedIndex) => {
    setSelectedCareer(selectedCareer);
    setSelectedCareerIndex(selectedIndex);
    setIsOpenModifyCareerDialog(true);
  };

  const closeModifyCareerDialog = async () => {
    setSelectedCareer({});
    setSelectedCareerIndex(null);
    setIsOpenModifyCareerDialog(false);
  };

  const openHideCareerDialog = async (e, selectedCareer, selectedIndex) => {
    e.stopPropagation();
    setSelectedCareer(selectedCareer);
    setSelectedCareerIndex(selectedIndex);
    setIsOpenHideCareerDialog(true);
  };

  const closeHideCareerDialog = async () => {
    setSelectedCareerIndex(null);
    setIsOpenHideCareerDialog(false);
  };

  const addCareer = async (career) => {
    const currentCareers = getValues().careers;
    currentCareers.push(career);
    setValue('careers', currentCareers);
  };

  const modifyCareer = async (career) => {
    const currentCareers = getValues().careers;
    const modifyCareer = currentCareers[career.index];
    currentCareers[career.index] = {...modifyCareer, ...career};
    setValue('careers', currentCareers);
  };

  const removeCareer = async (career) => {
    const currentCareers = getValues().careers;
    currentCareers.splice(career.index, 1);
    setValue('careers', currentCareers);
  };

  const hideCareer = async (career) => {
    const currentCareers = getValues().careers;
    const hideTarget = currentCareers[career.index];
    currentCareers[career.index] = {
      ...hideTarget,
      alias_name: career.alias_name,
      is_hidden_company_name: true,
      is_direct_input_company_name: career.is_direct_input_company_name,
      selected_size: career.selected_size,
      selected_domain: career.selected_domain,
    };
    setValue('careers', currentCareers);
  };

  const unHideCareer = async (career) => {
    const currentCareers = getValues().careers;
    const unHideTarget = currentCareers[career.index];
    currentCareers[career.index] = {
      ...unHideTarget,
      alias_name: null,
      is_hidden_company_name: false,
      is_direct_input_company_name: career.is_direct_input_company_name,
      selected_size: career.selected_size,
      selected_domain: career.selected_domain,
    };
    setValue('careers', currentCareers);
  };

  const careerDisplayName = (career) => {
    return career.is_hidden_company_name ? career.alias_name : career.company_name;
  };

  const careerDisplayPeriod = (career) => {
    return career.is_hidden_career_period ? '재직기간 비공개' : `${fKrDateYYYYMMKST(career.start_date)} ~ ${career.end_date === null ? '현재' : fKrDateYYYYMMKST(career.end_date)}`;
  };

  const showError = (e) => {
    const keys = Object.keys(e);
    const em = e[keys[0]].message;
    enqueueSnackbar(em, { variant: 'error', anchorOrigin: { vertical: 'bottom', horizontal: 'center' } });
  };

  const salaryCheckAndGo3Way = async () => {
    const formValues = getValues();
    
    // 유효성 검사 목록
    const validationErrors = [];
    
    // 경력 검증
    if (!formValues.careers || formValues.careers.length === 0) {
      validationErrors.push('경력 사항을 입력해주세요');
    }
    
    // 크루 소개 검증
    if (!formValues.comment) {
      validationErrors.push('크루 소개를 입력해주세요');
    } else if (formValues.comment.trim().length < 20) {
      validationErrors.push('크루 소개는 20자 이상 입력해주세요');
    }
    
    // 주당 업무 시간 검증
    if (!formValues.workingHoursPerWeek) {
      validationErrors.push('주당 업무 시간을 입력해주세요');
    } else if (formValues.workingHoursPerWeek > availableHours) {
      validationErrors.push(`주당 업무 시간은 현재 업무 가능 시간(${availableHours}시간) 이하로 입력해주세요`);
    }
    
    // 인건비 검증
    if (!salary || salary <= 0) {
      validationErrors.push('4주 인건비를 입력해주세요');
    } else if (salary > 100000) {
      validationErrors.push('인건비가 너무 높습니다. 인건비를 만원 단위로 입력해주세요');
    }
    
    // 포트폴리오 검증 (필요한 경우)
    if (isPortfolioRequiredPosition) {
      if (!formValues.checkPortfolio) {
        validationErrors.push('포트폴리오 공개 동의를 체크해주세요');
      }
      if (formValues.FOUportfolioType === "FOUportfolioURL" && !formValues.FOUportfolioURL) {
        validationErrors.push('포트폴리오 URL을 입력해주세요');
      }
      if (formValues.FOUportfolioType === "FOUportfolioFile" && !formValues.FOUportfolioFile?.name) {
        validationErrors.push('포트폴리오 파일을 업로드해주세요');
      }
    }
    
    // 사전 질문 답변 검증
    if (questions?.length > 0) {
      const answers = formValues.answers || [];
      
      for (let idx = 0; idx < questions.length; idx++) {
        const question = questions[idx];
        const answer = answers[idx];
        
        // yes-no 질문의 경우 form value에서 직접 체크
        if (question.question_type === "yes-no") {
          const answerynValue = formValues[`answeryn-${idx}`];
          if (!answerynValue) {
            validationErrors.push(`질문 ${idx + 1}번의 Yes/No 답변을 선택해주세요`);
          }
        }
        
        // 텍스트 답변이 없거나 50자 미만인 경우
        if (!answer || (answer?.trim().length || 0) < 50) {
          validationErrors.push(`질문 ${idx + 1}번의 답변을 50자 이상 작성해주세요`);
        }
      }
    }
    
    // 에러가 있으면 첫 번째 에러 메시지 표시
    if (validationErrors.length > 0) {
      enqueueSnackbar(validationErrors[0], { variant: 'warning' });
      setIsSubmitting(false);
      return;
    }
    
    // 모든 검증 통과 시 다음 단계로
    goCheck3Way();
  };

  return (
    <>
    {check3Way && (
      <Check3WayAvailableTime
        send={(v) => sendAccept(v)}
        isSubmitting={isSubmitting}
        previousTimes={previousTimes}
        isModify={isModify}
        meetingCount={meetingCount}
      />
    )}
    {!check3Way && (
    <Box>
      <FormProvider methods={methods}>
      {hasQuestion && (
        <Card sx={{ mt: 3 }}>
          <Box sx={{ p: 3 }}>
            <Stack alignItems="center" direction="row" spacing={1} sx={{ mb: 1 }}>
              <CircleNumber number={2} />
              <Typography variant="h5">
                <DIOText /> 인터뷰 답변하기
              </Typography>
            </Stack>
            <Typography>
              공고에 적합한 분인지 기업과 <DIOText />에서 검토하기 위한 질문입니다.
            </Typography>
            {questions.map((question, index) => (
              <Stack key={index} spacing={1} sx={{ mt: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={smUp ? 1 : 0}>
                  <Typography variant="h6" sx={{ color: "secondary.lighter" }}>
                    Q{index+1}.
                  </Typography>
                  <Typography variant="h6" sx={{ whiteSpace: "pre-wrap" }}>
                    {question.topic}
                  </Typography>
                </Stack>
                {question.question_type === "yes-no" ? (
                  <Stack>
                    <RadioGroup
                      row
                      name={`answeryn-${index}`}
                      value={answersyn[index]}
                      onChange={(e) => handleAnswersyn(e.target.value, index)}
                      sx={{ width: 1, display: 'flex', justifyContent: 'space-between', gap: 2 }}
                    >
                      {["네", "아니요"].map((option, opIndex) => {
                        return (
                          <FormControlLabel
                            sx={{
                              "&:hover": {background: (theme) => theme.palette.grey[200]},
                              background: (theme) => answersyn[index] === option && theme.palette.grey[200],
                              flexGrow: 1,
                              mx: 0,
                              py: 1.5,
                              px: 2,
                              my: 1,
                              border: (theme) => answersyn[index] === null ? `1px solid ${theme.palette.grey[500_32]}` : answersyn[index] === option ? '1px solid black' : `1px solid ${theme.palette.grey[500_32]}`,
                              borderRadius: 1,
                              alignItems: "center",
                              color: answersyn[index] === null ? "text.primary" : answersyn[index] === option ? "text.primary" : "text.disabled",
                            }}
                            key={`answeryn-${index}-${opIndex}`}
                            value={option}
                            control={<Radio sx={{ width: 20, height: 20, mr: 1.5 }} />}
                            label={option}
                          />
                        )}
                      )}
                    </RadioGroup>
                    {answersyn[index] ? (
                      <Stack>
                        <Typography variant="subtitle1" sx={{ my: 1, whiteSpace: "pre-wrap" }}>
                          {answersyn[index] === "네" ? question.yes_followup : question.no_followup}
                        </Typography>
                        <RHFTextField
                          name={`answers.${index}`}
                          placeholder="최소 50자 이상 작성해주세요"
                          multiline
                          minRows={3}
                          FormHelperTextProps={{ sx: { textAlign: "right" } }}
                          helperText={`${answersLength?.[index]}자${(answersLength?.[index] > 0 && answersLength?.[index] < 50) ? " / 50자 이상 작성해주세요" : ""}`}
                          error={answersLength?.[index] > 0 && answersLength?.[index] < 50}
                        />
                      </Stack>
                    ) : null}
                  </Stack>
                ) : (
                  <RHFTextField
                    name={`answers.${index}`}
                    placeholder="최소 50자 이상 작성해주세요"
                    multiline
                    minRows={3}
                    FormHelperTextProps={{ sx: { textAlign: "right" } }}
                    helperText={`${answersLength?.[index]}자${(answersLength?.[index] > 0 && answersLength?.[index] < 50) ? " / 50자 이상 작성해주세요" : ""}`}
                    error={answersLength?.[index] > 0 && answersLength?.[index] < 50}
                  />
                )}
              </Stack>
            ))}
          </Box>
        </Card>
      )}
        <Card sx={{ mt: 3 }}>
          <Box sx={{ p: 3 }}>
            <Stack alignItems="center" direction="row" spacing={1} sx={{ mb: 1 }}>
              <CircleNumber number={hasQuestion ? 3 : 2} />
              <Typography variant="h5">
                내 프로필 작성하기
              </Typography>
            </Stack>
            <Typography sx={{ mb: 3 }}>
              업데이트가 필요한 부분은 다시 작성해주세요.
            </Typography>

            <Stack spacing={3}>
              {/* careers */}
              <Stack sx={{ width: 1 }}>
                <Typography variant="h6">
                  경력 사항
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: "grey.600" }} gutterBottom>
                  현재 직장명 및 재직 기간을 노출하기를 원치 않는 경력의 경우 <b>가리기</b>를 눌러 수정해주세요.
                </Typography>
                <Stack spacing={2}>
                  {getValues().careers?.map((career, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: (theme) => `1px solid ${theme.palette.grey[500_32]}`,
                      '&:hover': {
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() => openModifyCareerDialog(career, index)}
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        display: 'flex',
                      }}
                    >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 2, overflow: "hidden", width: 1 }}>
                      <Typography sx={{ maxWidth: mdUp ? "100%" : smUp ? "300px" : "120px", textOverflow: 'ellipsis'}} noWrap>
                        {careerDisplayName(career)}
                      </Typography>
                      {mdUp && (
                        <Divider orientation="vertical" flexItem />
                      )}
                      {mdUp && (
                        <Typography sx={{ color: career?.is_hidden_career_period ? "text.disabled" : "text.primary" }}>
                          {careerDisplayPeriod(career)}
                        </Typography>
                      )}
                      <Button
                        size="small"
                        startIcon={<Iconify icon="eva:eye-off-fill" />}
                        sx={{ color: "text.secondary", px: 1, minWidth: career?.is_hidden_company_name ? 100 : 75 }}
                        onClick={(e) => openHideCareerDialog(e, career, index)}
                        disableFocusRipple
                      >
                        {career?.is_hidden_company_name ? '가리기 수정' : '가리기'}
                      </Button>
                    </Stack>
                    {mdUp && (
                      <Button size="small"
                        startIcon={<SvgIconStyle src={"/icons/icon-pencil-mono.svg"} sx={{ width: 16, height: 16 }} />}
                        sx={{ color: "text.secondary", px: 1, minWidth: 88 }}
                        onClick={(e) => openModifyCareerDialog(career, index)}
                        disableFocusRipple
                      >
                        경력 수정
                      </Button>
                    )}
                    {!mdUp && (
                      <IconButton
                        edge="end"
                        size="small"
                        sx={{ color: "text.secondary" }}
                        onClick={(e) => openModifyCareerDialog(career, index)}
                        disableFocusRipple
                      >
                        <SvgIconStyle src={"/icons/icon-pencil-mono.svg"} sx={{ width: 16, height: 16 }} />
                      </IconButton>
                    )}
                    </Box>
                    {!mdUp && (
                      <Typography variant="body2" sx={{ color: career?.is_hidden_career_period ? "text.disabled" : "text.primary" }}>
                        {careerDisplayPeriod(career)}
                      </Typography>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Typography sx={{ color: !!career?.description ? "text.secondary" : "text.disabled" }} noWrap>
                      {career?.description || '진행한 업무 상세 내용을 작성해주세요.'}
                    </Typography>
                  </Box>
                  ))}
                </Stack>
                {getValues().careers?.length === 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<Iconify icon="ic:round-plus" />}
                    color={errors.careers ? 'error' : 'primary'}
                    sx={{ justifyContent: 'start', width: '165px', borderRadius: 1 }}
                    onClick={openCareerDialog}
                  >
                    경력 사항 입력하기
                  </Button>
                )}
                {getValues().careers?.length > 0 && (
                <Button
                  variant="text"
                  startIcon={<Iconify icon="ic:round-plus" />}
                  sx={{ mt: 1, justifyContent: 'start', width: '155px', borderRadius: 1, color: 'text.secondary' }}
                  onClick={openCareerDialog}
                >
                  경력 사항 추가하기
                </Button>
                )}
                {errors.careers && getValues().careers?.length === 0 && (
                  <Typography variant="caption" sx={{ color: 'red', mt: 1 }}>
                    {errors.careers.message}
                  </Typography>
                )}
              </Stack>
              {/* careers */}
              <Stack sx={{ width: '100%' }} >
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  크루 소개
                </Typography>
                <Typography variant="body2" sx={{ color: (theme) => theme.palette.grey[600] }} gutterBottom>
                  현재 직장명을 노출하기를 원치 않으시면 추상적인 표현을 사용해주세요. (예. 시리즈C 스타트업)
                </Typography>
                <RHFTextField
                  name="comment"
                  multiline
                  minRows={2}
                  placeholder="크루님을 어필할 소개 멘트를 입력해주세요. (20자 이상)"
                  helperText="설정 페이지의 프로필에도 반영됩니다."
                />
              </Stack>
              {isPortfolioRequiredPosition && (
                <Stack direction="column" sx={{ width: '100%' }}>
                  <Typography variant="h6">
                    포트폴리오
                  </Typography>
                  <Typography variant='body2' sx={{ color: (theme) => theme.palette.grey[600] }}>
                    가입 시 제출한 포트폴리오가 회사에 전달될 예정이에요. <b>개인 정보가 노출되어 있는 경우</b> 수정한 포트폴리오로 제출해 주세요.
                  </Typography>
                  <RHFFileOrURL
                    fieldName="portfolio"
                    fieldLabel="포트폴리오"
                    titleText=""
                    defaultOption={portfolioType}
                    openFile={openFile}
                  />
                  <RHFCheckbox
                    name="checkPortfolio"
                    label="(필수) 제출한 포트폴리오가 매칭된 회사에 공개될 수 있음에 동의합니다."
                    sx={{ mt: 1, '& .MuiFormControlLabel-label' : {fontWeight: 'bold' }}}
                  />
                </Stack>
              )}
            </Stack>
          </Box>
        </Card>
        {isModify && (
          <Box
            id="offer-modify-info-box"
            sx={{ mt: 3, p: 2.5, backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08), borderRadius: 2, border: '1px solid', borderColor: "info.main" }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={smUp ? 1.5 : 1}>
              <Iconify icon={"eva:info-fill"} sx={{ minWidth: smUp ? 24 : 20, minHeight: smUp ? 24 : 20, width: smUp ? 24 : 20, height: smUp ? 24 : 20, color: 'info.main' }} />
              <Stack spacing={2}>
                <Stack>
                  <Typography variant={"subtitle1"} color="info.darker">
                    크루님, 업무 조건을 조정하시겠어요?
                  </Typography>
                  <Typography variant={"caption"} color="info.darker">
                    회사가 조정 요청한 내용을 확인하고, <b>업무 조건을 수정하거나 거절</b>해주세요.
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SvgIconStyle src="/icons/icon-pencil-mono.svg" sx={{ width: 16, height: 16, color: "info.dark" }} />
                    <Typography variant="subtitle2">
                      조정 요청한 조건
                    </Typography>
                  </Stack>
                  <Typography variant="body2">
                    {modifyItems}
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="eva:email-fill" sx={{ width: 16, height: 16, color: "info.dark" }} />
                    <Typography variant="subtitle2">
                      회사에서 보낸 메시지
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {modifyMesage}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        )}
        <Card sx={{ mt: 3 }}>
          <Box sx={{ p: 3 }}>
            <Stack spacing={3} sx={{ width: '100%' }}>
              <Stack spacing={2}>
                <Stack alignItems="center" direction="row" spacing={1}>
                  <CircleNumber number={hasQuestion ? 4 : 3} />
                  <Typography variant="h5">
                    업무 조건 작성하기
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  sx={{
                    maxWidth: 'max-content',
                  }}
                  fullWidth={false}
                  endIcon={<Iconify icon={'eva:chevron-right-fill'} />}
                  onClick={() => openWorkingTermsDialog()}
                  disableRipple
                >
                  다른 크루들은 어떻게 작성하나요?
                </Button>
              </Stack>

              <Stack sx={{ width: '100%' }} spacing={1}>
                <Typography variant="h6">
                  주당 업무 시간
                </Typography>
                <Stack>
                  <Typography variant="body2">
                    현재 업무 가능 시간: <InfoText>주 {availableHours}시간</InfoText>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.crew?.current_working_hours_per_week} / {user?.crew?.limit_working_hours_per_week}시간 일하는 중 {matchingHours ? ` • ${matchingHours}시간 계약 논의 중` : ''}
                  </Typography>
                </Stack>
                <RHFTextField
                  name="workingHoursPerWeek"
                  type="number"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">시간</InputAdornment>,
                  }}
                  placeholder="0"
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                      e.preventDefault();
                    }
                  }}
                />
              </Stack>

              {!newPositions.includes(user?.crew?.position) && (
              <Stack sx={{ width: '100%' }} spacing={1}>
                <Typography variant="h6" gutterBottom>
                  4주 인건비
                </Typography>
                {/* 차트와 인풋 */}
                <BGAccordion expanded={expand}>
                  <NoBGAccordionSummary
                    expandIcon={<IconButton onClick={toggleAccordion}>
                      <ExpandMoreIcon />
                    </IconButton>
                    }
                  >
                    <TextField
                      variant="standard"
                      fullWidth
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                          e.preventDefault();
                        }
                      }}
                      value={salary}
                      onChange={(e) => handleSalary(e)}
                      placeholder="000"
                      InputProps={{
                        endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>만원</InputAdornment>,
                        disableUnderline: true,
                      }}
                    />
                  </NoBGAccordionSummary>
                  <BGAccordionDetails>
                    {isLoading
                      ? <Skeleton variant="rectangular" height={smUp ? 360 : 280} />
                      : <AverageSalaryChart application={application} amount={amount} detailIndex={detailIndex} />
                    }
                    <Stack display="flex" sx={{ width: 1 }}>
                      <FormControlLabel
                        sx={{
                          mx: 0,
                          justifyContent: 'flex-end',
                          '& .MuiFormControlLabel-label' : {color: (theme) => theme.palette.grey[600], fontWeight: 'bold' },
                        }}
                        control={
                          <Checkbox checked={averageApplied} onClick={() => checkAverageSalary()}/>
                        }
                        label="평균 인건비 적용하기"
                      />
                    </Stack>
                  </BGAccordionDetails>
                </BGAccordion>

                <Button
                  size="small"
                  sx={{
                    maxWidth: 'max-content',
                    color: 'text.secondary',
                  }}
                  fullWidth={false}
                  startIcon={<Iconify icon={'eva:info-fill'} />}
                  onClick={() => openFeeTaxDialog()}
                >
                  수수료 & 세금 정책
                </Button>
              </Stack>
              )}

              {newPositions.includes(user?.crew?.position) && (
              <Stack sx={{ width: '100%' }} spacing={1}>
                <Typography variant="h6" gutterBottom>
                  4주 인건비
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                      e.preventDefault();
                    }
                  }}
                  value={salary}
                  onChange={(e) => handleSalary(e)}
                  placeholder="000"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">만원</InputAdornment>,
                    disableUnderline: true,
                  }}
                />
              </Stack>
              )}

              <BackGroundStyle>
                <Stack sx={{ width: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .5 }}>
                    <SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ color: "text.secondary", width: 16, height: 16, minWidth: 16, minHeight: 16 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      2주, 8주 플랜 안내
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mb: .5 }}>
                    회사는 2주, 4주, 8주의 계약기간 중 하나를 선택하고 미팅에 참여합니다.
                  </Typography>
                  <ul style={{ marginLeft: "16px" }}>
                    <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                      <Typography variant="caption">
                        2주 또는 8주 계약시 크루가 제안한 4주 인건비를 기준으로 기간에 맞게 조정합니다. (2주: 50%, 8주: 200%)
                      </Typography>
                    </li>
                    <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                      <Typography variant="caption">
                        모든 플랜의 주당 업무 시간은 동일합니다.
                      </Typography>
                    </li>
                    <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                      <Typography variant="caption">
                        플랜은 미팅 후 협의하여 변경할 수 있어요.
                      </Typography>
                    </li>
                    <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                      <Typography variant="caption">
                        상주 업무는 회사에서 4주, 8주 플랜만 선택할 수 있어요.
                      </Typography>
                    </li>
                  </ul>
                </Stack>
              </BackGroundStyle>

              <RHFTextField
                name="suggestion"
                label={"질문 또는 업무 범위 수정 제안 (선택)"}
                labelVariant="h6"
                fullWidth
                multiline
                minRows={2}
                placeholder="기업에게 질문 또는 업무 범위에 대해 작성할 수 있어요."
              />
            </Stack>
          </Box>
        </Card>
        <BackGroundStyle>
          <Typography variant="body2" sx={{ color: 'primary.darker' }}>
            Zoom 사전 미팅은 미팅 위반 행위(크루의 개인정보를 묻는 행위, 욕설 등) 체크를 위해 레코딩되며, 7일간 저장 후 파기됩니다. <br />
            동의하시는 경우에만 사전 미팅을 수락해 주세요.
          </Typography>
        </BackGroundStyle>
        <Stack alignItems="flex-end" sx={{ mt: 3 }}>
          {smUp && (
            <Stack direction="row">
              {!isOpenJobPost && (
              <LoadingButton size="large" type="button" loading={isSubmitting} onClick={openPassDialog} sx={{ mr: 3 }}>
                {coffeeChat ? "파트타임 제안을 거절합니다" : "다른 제안을 원합니다"}
              </LoadingButton>
              )}

              <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting} onClick={handleSubmit(salaryCheckAndGo3Way, showError)}>
                {isOpenJobPost ? "공고 지원하고 미팅 예약하기" : "제안을 수락하고 미팅 예약하기"}
              </LoadingButton>
            </Stack>
          )}

          {!smUp && (
            <Stack spacing={2} sx={{ width: 1 }}>
            <LoadingButton size="large" fullWidth type="submit" variant="contained" loading={isSubmitting} onClick={handleSubmit(salaryCheckAndGo3Way, showError)}>
              {isOpenJobPost ? "공고 지원하고 미팅 예약하기" : "제안을 수락하고 미팅 예약하기"}
            </LoadingButton>

            {!isOpenJobPost && (
            <LoadingButton size="large" fullWidth type="button" loading={isSubmitting} onClick={openPassDialog}>
              {coffeeChat ? "파트타임 제안을 거절합니다" : "다른 제안을 원합니다"}
            </LoadingButton>
            )}
            </Stack>
          )}
        </Stack>
      </FormProvider>
      <OfferPassDialog passDialog={passDialog} closePassDialog={closePassDialog} reloadOffer={reloadOffer} />
      <WorkingTermsDialog isOpen={isOpenWorkingTermsDialog} close={() => setIsOpenWorkingTermsDialog(false)} />
      <FeeTaxDialog isOpen={isOpenFeeTaxDialog} close={() => setIsOpenFeeTaxDialog(false)} />
      <EasySalaryDialog isOpen={isOpenEasySalaryDialog} close={() => setIsOpenEasySalaryDialog(false)} />
      <CannotApplyAverageSalaryDialog isOpen={isOpenCannotApplyAverageSalaryDialog} close={() => setIsOpenCannotApplyAverageSalaryDialog(false)} />
      <AddCareerDialog
        isOpen={isOpenAddCareerDialog}
        closeDialog={closeAddCareerDialog}
        addCareer={addCareer}
        hasWorkingCareer={hasWorkingCareer}
      />
      <ModifyCareerDialog
        isOpen={isOpenModifyCareerDialog}
        closeDialog={closeModifyCareerDialog}
        modifyCareer={modifyCareer}
        removeCareer={removeCareer}
        career={selectedCareer}
        careerIndex={selectedCareerIndex}
        hasWorkingCareer={hasWorkingCareer}
      />
      <HideCareerDialog
        isOpen={isOpenHideCareerDialog}
        closeDialog={closeHideCareerDialog}
        career={selectedCareer}
        hideCareer={hideCareer}
        unHideCareer={unHideCareer}
        careerIndex={selectedCareerIndex}
      />
    </Box>
    )}
    </>
  );
}
